/*
 * PipelineTracePanel — iter36 msg515+msg516
 * -----------------------------------------
 * Real-time per-exchange × per-side view of the LAST QUOTE DECISION
 * produced by the market-maker loop. Answers "why is OPEN_ORDERS=0?"
 * without leaving the dashboard.
 *
 * Data source: state.last_order_decision_by_exchange (populated by
 * bot._record_order_decision on every quote attempt, guard block,
 * intention skip, exchange rejection or SUBMITTED / SIMULATED order).
 *
 * DRY-RUN outputs are labelled SIMULATED explicitly so an operator can
 * never confuse a local timestamp with a real Coinstore order_id.
 */
import { useMemo } from "react";

const DECISION_STYLE = {
  SUBMITTED:            { fg: "#7EE787", label: "SUBMITTED" },
  SIMULATED:            { fg: "#F0C674", label: "SIMULATED · DRY-RUN" },
  BLOCKED_PRICE_GUARD:  { fg: "#F97583", label: "BLOCKED · PRICE GUARD" },
  SKIPPED_INTENTION:    { fg: "#B392F0", label: "SKIPPED · INTENTION" },
  EXCHANGE_REJECTED:    { fg: "#F97583", label: "EXCHANGE REJECTED" },
};

function DecisionCell({ dec }) {
  if (!dec) {
    return (
      <div
        data-testid="pipeline-trace-empty"
        style={{ color: "#8b949e", fontSize: 12, padding: "12px 0" }}
      >
        —
      </div>
    );
  }
  const style = DECISION_STYLE[dec.decision] || { fg: "#c9d1d9", label: dec.decision };
  const notional =
    typeof dec.notional_usd === "number" ? dec.notional_usd.toFixed(4) : "-";
  const price = typeof dec.price === "number" ? dec.price.toPrecision(8) : "-";
  const qty = typeof dec.qty === "number" ? dec.qty.toPrecision(6) : "-";
  return (
    <div
      data-testid={`pipeline-trace-cell-${dec.exchange}-${dec.side}`}
      style={{ padding: "10px 12px", borderRadius: 6, background: "rgba(255,255,255,0.02)" }}
    >
      <div
        data-testid={`pipeline-trace-decision-${dec.exchange}-${dec.side}`}
        style={{ color: style.fg, fontWeight: 700, fontSize: 12, letterSpacing: 0.6 }}
      >
        {style.label}
      </div>
      <div style={{ color: "#c9d1d9", fontSize: 12, marginTop: 6 }}>
        <span style={{ color: "#8b949e" }}>PRICE </span>
        <span data-testid={`pipeline-trace-price-${dec.exchange}-${dec.side}`}>{price}</span>
        <span style={{ color: "#8b949e", marginLeft: 10 }}>QTY </span>
        <span data-testid={`pipeline-trace-qty-${dec.exchange}-${dec.side}`}>{qty}</span>
        <span style={{ color: "#8b949e", marginLeft: 10 }}>NOTIONAL </span>
        <span data-testid={`pipeline-trace-notional-${dec.exchange}-${dec.side}`}>${notional}</span>
      </div>
      <div style={{ color: "#8b949e", fontSize: 11, marginTop: 6 }}>
        <span style={{ color: "#8b949e" }}>REASON </span>
        <span
          data-testid={`pipeline-trace-reason-${dec.exchange}-${dec.side}`}
          style={{ color: "#c9d1d9" }}
        >
          {dec.reason || "-"}
        </span>
      </div>
      {dec.simulated_order_id ? (
        <div
          data-testid={`pipeline-trace-simulated-id-${dec.exchange}-${dec.side}`}
          style={{ color: "#F0C674", fontSize: 11, marginTop: 4, fontFamily: "monospace" }}
        >
          {dec.simulated_order_id}
        </div>
      ) : null}
      {dec.exchange_order_id ? (
        <div
          data-testid={`pipeline-trace-exchange-id-${dec.exchange}-${dec.side}`}
          style={{ color: "#7EE787", fontSize: 11, marginTop: 4, fontFamily: "monospace" }}
        >
          EXCHANGE_ORDER_ID: {dec.exchange_order_id}
        </div>
      ) : null}
      {dec.client_order_id ? (
        <div style={{ color: "#8b949e", fontSize: 10, marginTop: 2, fontFamily: "monospace" }}>
          coid: {dec.client_order_id}
        </div>
      ) : null}
    </div>
  );
}

export default function PipelineTracePanel({ state }) {
  const buckets = state?.last_order_decision_by_exchange || {};
  const sizingByEx = state?.adaptive_sizing || {};
  const activeExchanges = useMemo(
    () => (state?.active_exchanges || []).filter((x) => x.enabled).map((x) => x.name),
    [state?.active_exchanges],
  );
  const pg = state?.price_guard || {};

  return (
    <div
      data-testid="pipeline-trace-panel"
      style={{
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 8,
        padding: 16,
        background: "rgba(255,255,255,0.02)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ color: "#c9d1d9", fontSize: 13, letterSpacing: 1, textTransform: "uppercase" }}>
          Pipeline Trace · Last Quote Decision per Exchange × Side
        </div>
        <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#8b949e" }}>
          <span data-testid="pipeline-trace-zone">
            ZONE: <b style={{ color: "#c9d1d9" }}>{pg.zone || "SAFE"}</b>
          </span>
          <span data-testid="pipeline-trace-allowed-side">
            ALLOWED_SIDE: <b style={{ color: "#c9d1d9" }}>{pg.allowed_side || "BOTH"}</b>
          </span>
          <span data-testid="pipeline-trace-guard-mode">
            GUARD: <b style={{ color: pg.dry_run ? "#F0C674" : "#7EE787" }}>{pg.dry_run ? "DRY-RUN" : "LIVE"}</b>
          </span>
        </div>
      </div>

      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "minmax(240px,280px) 1fr 1fr", gap: 12 }}>
        <div style={{ color: "#8b949e", fontSize: 11 }}>EXCHANGE · SIZING</div>
        <div style={{ color: "#F97583", fontSize: 11, letterSpacing: 0.6 }}>SELL SIDE</div>
        <div style={{ color: "#7EE787", fontSize: 11, letterSpacing: 0.6 }}>BUY SIDE</div>

        {activeExchanges.map((name) => {
          const bucket = buckets[name] || {};
          const sz = sizingByEx[name] || {};
          const hardFail = sz.hard_fail === "INSUFFICIENT_TRADABLE_CAPITAL";
          return (
            <>
              <div
                key={`${name}-name`}
                data-testid={`pipeline-trace-row-${name}`}
                style={{ color: "#c9d1d9", fontSize: 13, alignSelf: "start", fontWeight: 600, textTransform: "uppercase", padding: "10px 0" }}
              >
                {name}
                <div style={{ color: "#8b949e", fontSize: 11, fontWeight: 400 }}>
                  {state?.symbol || "-"}
                </div>
                <div style={{ color: "#8b949e", fontSize: 10, fontWeight: 400, marginTop: 6, lineHeight: 1.5 }}>
                  {sz.available_usd !== undefined && (
                    <div data-testid={`sizing-avail-usd-${name}`}>
                      AVAIL {sz.quote_symbol || "USDT"}: <b style={{ color: "#c9d1d9" }}>${(sz.available_usd ?? 0).toFixed(4)}</b>
                    </div>
                  )}
                  {sz.available_base !== undefined && sz.available_base_symbol && (
                    <div data-testid={`sizing-avail-base-${name}`}>
                      AVAIL {sz.available_base_symbol}: <b style={{ color: "#c9d1d9" }}>{(sz.available_base ?? 0).toFixed(4)}</b>
                    </div>
                  )}
                  {sz.min_notional !== undefined && (
                    <div data-testid={`sizing-min-notional-${name}`}>
                      MIN NOTIONAL: <b style={{ color: "#F0C674" }}>${(sz.min_notional ?? 0).toFixed(2)}</b>
                    </div>
                  )}
                  {sz.layers !== undefined && (
                    <div data-testid={`sizing-layers-${name}`}>
                      LAYERS: <b style={{ color: hardFail ? "#F97583" : "#7EE787" }}>{sz.layers}</b>
                      {" · $/LAYER: "}
                      <b style={{ color: hardFail ? "#F97583" : "#c9d1d9" }}>${(sz.usd_per_layer ?? 0).toFixed(4)}</b>
                    </div>
                  )}
                  {sz.capital_utilization_pct !== undefined && (
                    <div data-testid={`sizing-util-${name}`}>
                      CAPITAL UTIL: <b style={{ color: "#c9d1d9" }}>{(sz.capital_utilization_pct ?? 0).toFixed(2)}%</b>
                    </div>
                  )}
                  {hardFail && (
                    <div
                      data-testid={`sizing-hard-fail-${name}`}
                      style={{ color: "#F97583", marginTop: 4, fontWeight: 700 }}
                    >
                      INSUFFICIENT_TRADABLE_CAPITAL
                    </div>
                  )}
                  {sz.dry_run_active && sz.dry_run_simulated_balance_usd > 0 && (
                    <div style={{ color: "#F0C674", marginTop: 4, fontStyle: "italic" }}>
                      DRY-RUN SIM BAL: ${(sz.dry_run_simulated_balance_usd ?? 0).toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
              <DecisionCell key={`${name}-sell`} dec={bucket.sell} />
              <DecisionCell key={`${name}-buy`} dec={bucket.buy} />
            </>
          );
        })}
        {activeExchanges.length === 0 && (
          <div style={{ gridColumn: "1 / -1", color: "#8b949e", fontSize: 12 }}>
            No active exchange in the registry — enable at least one via ConfigEditor.
          </div>
        )}
      </div>
    </div>
  );
}
